/**
 * Components for the `video` module kind (F3-F): authoring editor (choose/upload a
 * `video_asset` → saves `config.videoAssetId`) and consumption viewer (the module's
 * standalone player). Local-first: the config travels in `course_module.config`
 * (same author/editor write-path). The plugin registers in the web module
 * registry (without touching the authoring/player core).
 */
import { type Component, createMemo, createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Film, Video } from "@fleet/icons";
import { updateCourseModuleMutation, validateVideoModuleConfig } from "@space/shared";
import type { CourseModule } from "@space/powersync";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useCourseModules } from "@/features/academy/hooks/use-course-modules";
import type { ModuleComponentProps } from "@/features/academy/modules/registry";
import { VideoPlayer } from "./video-player";
import { VideoPickerDialog } from "./video-picker-dialog";
import { useVideoAsset } from "../hooks/use-media";

/** Reads `config.videoAssetId` from a `course_module` row (tolerant JSON). */
function videoAssetIdOf(module: CourseModule | undefined): string {
  if (!module?.config) {
    return "";
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(module.config);
  } catch {
    return "";
  }
  const result = validateVideoModuleConfig(parsed);
  return result.ok ? (result.value.videoAssetId ?? "") : "";
}

function useModuleRow(props: ModuleComponentProps) {
  const modules = useCourseModules(() => props.courseId);
  const row = createMemo<CourseModule | undefined>(() =>
    (modules.data ?? []).find((m) => m.id === props.moduleId),
  );
  return row;
}

/** `video` module's authoring editor: choose/upload the video to play. */
export const VideoModuleEditor: Component<ModuleComponentProps> = (props) => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const row = useModuleRow(props);
  const [pickerOpen, setPickerOpen] = createSignal(false);
  const videoId = createMemo(() => videoAssetIdOf(row()));
  const asset = useVideoAsset(() => videoId());

  const setVideo = (id: string) => {
    const db = drizzle();
    if (db) {
      void updateCourseModuleMutation(db, props.moduleId, {
        config: { videoAssetId: id },
      }).catch((err: unknown) => {
        console.error("Error setting video:", err);
      });
    }
  };

  return (
    <div class="space-y-2 rounded-lg bg-muted/30 p-3" data-testid="video-module-editor">
      <Show
        when={videoId()}
        fallback={<p class="text-xs text-muted-foreground">{t(msg`No video selected yet.`)}</p>}
      >
        <div class="flex items-center gap-2 text-sm">
          <Film class="h-4 w-4 text-accent" />
          <span class="truncate" data-testid="video-module-title">
            {asset.data?.title ?? t(msg`Selected video`)}
          </span>
        </div>
      </Show>
      <Button
        variant="outline"
        size="sm"
        class="gap-1.5"
        data-testid="video-module-pick"
        onClick={() => setPickerOpen(true)}
      >
        <Video class="h-4 w-4" />
        {videoId() ? t(msg`Change video`) : t(msg`Choose video`)}
      </Button>
      <VideoPickerDialog
        open={pickerOpen()}
        onOpenChange={setPickerOpen}
        courseId={props.courseId}
        onPick={setVideo}
      />
    </div>
  );
};

/** `video` module's consumption viewer: standalone player. */
export const VideoModuleViewer: Component<ModuleComponentProps> = (props) => {
  const { t } = useI18n();
  const row = useModuleRow(props);
  const videoId = createMemo(() => videoAssetIdOf(row()));
  const asset = useVideoAsset(() => videoId());
  return (
    <Show
      when={videoId()}
      fallback={
        <p class="text-sm text-muted-foreground" data-testid="video-module-empty">
          {t(msg`This video is not available yet.`)}
        </p>
      }
    >
      <div class="space-y-2" data-testid="video-module-viewer">
        <Show when={asset.data?.title}>
          <h3 class="font-display text-base font-bold">{asset.data?.title}</h3>
        </Show>
        <VideoPlayer
          videoId={videoId()}
          title={asset.data?.title ?? undefined}
          durationSeconds={asset.data?.duration_seconds ?? null}
        />
      </div>
    </Show>
  );
};
